/**
 * ApprovalQueuePage — unified cross-service approvals queue.
 *
 * Surfaces every domain that owns an approval workflow in NIPOST UPMP:
 *   - service-mail        : address-change requests (cross-zone)
 *   - service-finance     : settlement approvals (PENDING → APPROVED → PAID)
 *   - service-franchise   : franchise applications (PENDING → APPROVED|REJECTED)
 *   - service-franchise   : tier upgrades / downgrades
 *
 * Data path: GET /v1/approvals on service-reporting-api (aggregator), which
 * fans out to each owning service over m2m HTTP. Approve / reject also funnel
 * through the aggregator which delegates to the owning service's existing
 * mutation. No new schema; this is purely composition over the four existing
 * approval lifecycles.
 *
 * Permissions required:
 *   Approval/read, Approval/approve, Approval/reject — seeded to
 *   platform-admin + dev-superadmin-role by
 *   prisma/identity/db-migrations/20260512_approvals_perms.sql.
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  ListChecks,
  X,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  APPROVAL_TYPE_LABEL,
  APPROVAL_TYPES,
  type ApprovalRowDto,
  type ApprovalStatus,
  type ApprovalType,
  approveItem,
  listApprovals,
  rejectItem,
  trackingNumberFor,
} from '../../lib/api/approvals';
import { PageHeader } from '../shared/StaffUI';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeAge(isoString: string): string {
  const t = new Date(isoString).getTime();
  if (Number.isNaN(t)) return '—';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'in the future';
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffH < 1) return '<1h ago';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
}

function formatMinor(amountMinor: string | undefined): string | null {
  if (!amountMinor) return null;
  const n = Number(amountMinor);
  if (!Number.isFinite(n)) return null;
  const major = n / 100;
  return `₦${major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_TINT: Record<ApprovalStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-emerald-50 text-emerald-700',
  REJECTED: 'bg-red-50 text-red-700',
};

const TYPE_TINT: Record<ApprovalType, string> = {
  MAIL_ADDRESS_CHANGE: 'bg-blue-50 text-blue-700',
  SETTLEMENT: 'bg-purple-50 text-purple-700',
  FRANCHISE_APPLICATION: 'bg-teal-50 text-teal-700',
  FRANCHISE_TIER_UPGRADE: 'bg-indigo-50 text-indigo-700',
};

const STATUS_OPTIONS: { value: ApprovalStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

// ---------------------------------------------------------------------------
// Reject dialog (modal)
// ---------------------------------------------------------------------------

function RejectDialog({
  row,
  onConfirm,
  onCancel,
  loading,
}: {
  row: ApprovalRowDto;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  loading: boolean;
}): JSX.Element {
  const [reason, setReason] = useState('');
  return (
    <div
      data-testid="reject-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white border border-border shadow-xl p-5">
        <h3 className="text-base font-bold text-gray-900">Reject approval</h3>
        <p className="mt-1 text-xs text-muted">
          {APPROVAL_TYPE_LABEL[row.type]} · <span className="font-mono">{row.subjectLabel}</span>
        </p>
        <label className="mt-3 block text-xs font-semibold text-gray-700">
          Rejection reason (required)
        </label>
        <textarea
          data-testid="rejection-reason-input"
          className="mt-1 w-full text-sm border border-border rounded-lg p-2 min-h-[96px] resize-none focus:outline-none focus:ring-1 focus:ring-red-400"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this request is being rejected…"
          autoFocus
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-semibold text-gray-700 border border-border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="reject-confirm-btn"
            disabled={reason.trim().length === 0 || loading}
            onClick={() => onConfirm(reason.trim())}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-700 text-white text-sm font-semibold rounded-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Rejecting…' : 'Confirm reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast (minimal inline; replaces external toast lib)
// ---------------------------------------------------------------------------

interface ToastState {
  kind: 'success' | 'error';
  message: string;
}

function ToastBanner({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }): JSX.Element {
  const tint =
    toast.kind === 'success'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-red-50 border-red-200 text-red-800';
  return (
    <div
      data-testid={`toast-${toast.kind}`}
      className={`mb-3 flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${tint}`}
    >
      <span>{toast.message}</span>
      <button
        type="button"
        className="text-xs font-semibold underline opacity-70 hover:opacity-100"
        onClick={onDismiss}
      >
        dismiss
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

export function ApprovalQueuePage(): JSX.Element {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ApprovalStatus>('PENDING');
  const [typeFilter, setTypeFilter] = useState<ApprovalType | 'ALL'>('ALL');
  const [page, setPage] = useState(1);
  const [rejecting, setRejecting] = useState<ApprovalRowDto | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  function toggleExpanded(rowKey: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }

  const queryKey = useMemo(
    () => ['approvals', { status, type: typeFilter, page }] as const,
    [status, typeFilter, page],
  );

  const q = useQuery({
    queryKey,
    queryFn: () =>
      listApprovals({
        status,
        ...(typeFilter !== 'ALL' ? { type: typeFilter } : {}),
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['approvals'] });
  };

  const approveM = useMutation({
    mutationFn: approveItem,
    onSuccess: (row) => {
      invalidate();
      setToast({
        kind: 'success',
        message: `Approved ${APPROVAL_TYPE_LABEL[row.type]}: ${row.subjectLabel}`,
      });
    },
    onError: (err) => {
      setToast({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Approve failed',
      });
    },
  });

  const rejectM = useMutation({
    mutationFn: rejectItem,
    onSuccess: (row) => {
      setRejecting(null);
      invalidate();
      setToast({
        kind: 'success',
        message: `Rejected ${APPROVAL_TYPE_LABEL[row.type]}: ${row.subjectLabel}`,
      });
    },
    onError: (err) => {
      setToast({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Reject failed',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Permission denied / error
  // -------------------------------------------------------------------------

  const denied =
    q.isError && q.error instanceof ApiError && (q.error.status === 401 || q.error.status === 403);

  if (denied) {
    return (
      <div>
        <PageHeader
          title="Approval Queue"
          description="Cross-service approvals across mail, finance settlements, franchise applications, and tier upgrades."
        />
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
          <p className="font-semibold">Permission required</p>
          <p className="mt-1">
            Your role does not include <code className="font-mono">Approval/read</code>. Ask a
            platform admin to grant this permission (seed:
            <code className="ml-1 font-mono">20260512_approvals_perms.sql</code>).
          </p>
        </div>
      </div>
    );
  }

  if (q.isError && !denied) {
    return (
      <div>
        <PageHeader
          title="Approval Queue"
          description="Cross-service approvals across mail, finance settlements, franchise applications, and tier upgrades."
        />
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
          <p className="font-semibold">Could not load approval queue</p>
          <p className="mt-1">
            {q.error instanceof Error ? q.error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title="Approval Queue"
        description="Cross-service approvals across mail, finance settlements, franchise applications, and tier upgrades."
      />

      {toast && <ToastBanner toast={toast} onDismiss={() => setToast(null)} />}

      {/* Filters */}
      <div
        data-testid="approvals-filters"
        className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 bg-white border border-border rounded-xl p-3"
      >
        <div>
          <label className="text-[10px] font-bold text-muted uppercase tracking-wider">Status</label>
          <div className="mt-1 flex flex-wrap gap-1">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                data-testid={`status-${opt.value}`}
                onClick={() => {
                  setStatus(opt.value);
                  setPage(1);
                }}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
                  status === opt.value
                    ? 'bg-nipost-green text-white'
                    : 'bg-white border border-border text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-bold text-muted uppercase tracking-wider">Type</label>
          <div className="mt-1 flex flex-wrap gap-1">
            <button
              type="button"
              data-testid="type-ALL"
              onClick={() => {
                setTypeFilter('ALL');
                setPage(1);
              }}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
                typeFilter === 'ALL'
                  ? 'bg-nipost-green text-white'
                  : 'bg-white border border-border text-gray-700 hover:bg-gray-50'
              }`}
            >
              All sources
            </button>
            {APPROVAL_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`type-${t}`}
                onClick={() => {
                  setTypeFilter(t);
                  setPage(1);
                }}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
                  typeFilter === t
                    ? 'bg-nipost-green text-white'
                    : 'bg-white border border-border text-gray-700 hover:bg-gray-50'
                }`}
              >
                {APPROVAL_TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Result count + refresh state */}
      <div className="mb-2 flex items-center justify-between text-[11px] text-muted">
        <span data-testid="approvals-count">
          {q.isLoading ? 'Loading…' : `${total} matching approval${total === 1 ? '' : 's'}`}
        </span>
        <span>{q.isFetching && !q.isLoading ? 'Refreshing…' : ''}</span>
      </div>

      {/* List */}
      {q.isLoading ? (
        <div className="h-32 bg-white border border-border rounded-xl animate-pulse flex items-center justify-center">
          <p className="text-sm text-muted">Loading approvals…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-10 text-center">
          <ListChecks className="w-8 h-8 text-muted mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-700">Inbox zero</p>
          <p className="text-xs text-muted mt-1">
            No approvals match the current filter.
          </p>
        </div>
      ) : (
        <div
          data-testid="approval-queue-list"
          className="bg-white border border-border rounded-xl overflow-hidden"
        >
          {items.map((row) => {
            const rowKey = `${row.type}:${row.id}`;
            const isBusy =
              (approveM.isPending && approveM.variables?.id === row.id) ||
              (rejectM.isPending && rejectM.variables?.id === row.id);
            const amount = formatMinor(row.amountMinor);
            const isExpanded = expanded.has(rowKey);
            return (
              <div
                key={rowKey}
                data-testid={`approval-row-${row.id}`}
                className="border-b border-border last:border-b-0"
              >
                <div className="p-3 flex flex-col md:flex-row md:items-center md:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded ${STATUS_TINT[row.status]}`}
                      >
                        <Clock className="w-3 h-3" /> {row.status}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded ${TYPE_TINT[row.type]}`}
                      >
                        {APPROVAL_TYPE_LABEL[row.type]}
                      </span>
                      <span className="text-[10px] text-muted">{relativeAge(row.submittedAt)}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {row.subjectLabel}
                    </p>
                    <p className="text-[11px] text-muted truncate">
                      Submitted by{' '}
                      <span className="font-mono">{row.submittedBy || '—'}</span>
                      {amount ? <> · <span className="font-semibold text-amber-700">{amount}</span></> : null}
                      <span className="ml-1 text-muted">· id <span className="font-mono">{row.id.slice(0, 8)}</span></span>
                    </p>
                  </div>

                  <div className="mt-2 md:mt-0 flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      data-testid={`details-btn-${row.id}`}
                      aria-expanded={isExpanded}
                      onClick={() => toggleExpanded(rowKey)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-border text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50"
                    >
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {isExpanded ? 'Hide details' : 'Show details'}
                    </button>
                    {row.status === 'PENDING' && (
                      <>
                        <button
                          type="button"
                          data-testid={`approve-btn-${row.id}`}
                          disabled={isBusy}
                          onClick={() =>
                            approveM.mutate({
                              id: row.id,
                              type: row.type,
                              trackingNumber: trackingNumberFor(row),
                            })
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Check className="w-4 h-4" />
                          {approveM.isPending && approveM.variables?.id === row.id ? 'Approving…' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          data-testid={`reject-btn-${row.id}`}
                          disabled={isBusy}
                          onClick={() => setRejecting(row)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-300 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          <X className="w-4 h-4" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isExpanded ? (
                  <div className="px-3 pb-3" data-testid={`details-panel-${row.id}`}>
                    <DetailsPanel row={row} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-xs text-gray-700">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="page-prev"
              disabled={page <= 1 || q.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-3 h-3" /> Prev
            </button>
            <button
              type="button"
              data-testid="page-next"
              disabled={page >= totalPages || q.isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Next <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Gap notice for non-PENDING + mail */}
      {status !== 'PENDING' && (typeFilter === 'ALL' || typeFilter === 'MAIL_ADDRESS_CHANGE') && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            service-mail does not currently expose a list of historical (APPROVED / REJECTED)
            address-change requests — only the pending queue. This filter therefore omits mail
            decisions until that downstream endpoint is added.
          </span>
        </div>
      )}

      {/* Reject modal */}
      {rejecting && (
        <RejectDialog
          row={rejecting}
          loading={rejectM.isPending}
          onConfirm={(reason) =>
            rejectM.mutate({
              id: rejecting.id,
              type: rejecting.type,
              reason,
              trackingNumber: trackingNumberFor(rejecting),
            })
          }
          onCancel={() => setRejecting(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-row expandable details panel.
//
// The aggregator at service-reporting-api hands back a `metadata` blob shaped
// per type (see apps/service-reporting-api/src/modules/approvals/
// approvals.service.ts shape* helpers). We render a tailored, human-readable
// view for each type, with a JSON fallback for any keys we don't explicitly
// surface so reviewers never miss data.
// ---------------------------------------------------------------------------

function readStr(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

function readNgnFromString(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  if (typeof v !== 'string' || v.length === 0) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `₦${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function readNgnFromMinor(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  let minor: number | null = null;
  if (typeof v === 'number') minor = v;
  else if (typeof v === 'string' && v.length > 0) {
    const parsed = Number(v);
    if (Number.isFinite(parsed)) minor = parsed;
  }
  if (minor === null) return null;
  const major = minor / 100;
  return `₦${major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Field({ label, value }: { readonly label: string; readonly value: string | null }): JSX.Element {
  return (
    <div>
      <p className="text-[10px] uppercase text-muted font-semibold tracking-wide">{label}</p>
      <p className="text-xs text-gray-900 mt-0.5 break-words">{value ?? <span className="text-muted italic">—</span>}</p>
    </div>
  );
}

/**
 * Renders any metadata keys we did NOT explicitly surface as a small KV list.
 * Pure escape hatch so reviewers never miss data when the aggregator is
 * extended without a corresponding frontend update.
 */
function MetadataResidue({
  meta,
  surfaced,
}: {
  readonly meta: Record<string, unknown>;
  readonly surfaced: ReadonlySet<string>;
}): JSX.Element | null {
  const extras = Object.entries(meta).filter(([k, v]) => !surfaced.has(k) && v !== null && v !== undefined && v !== '');
  if (extras.length === 0) return null;
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-[10px] uppercase text-muted font-semibold tracking-wide hover:text-gray-700">
        Other fields ({extras.length})
      </summary>
      <pre className="mt-2 p-2 bg-white border border-border rounded-lg text-[10px] font-mono whitespace-pre-wrap break-all overflow-x-auto">
        {JSON.stringify(Object.fromEntries(extras), null, 2)}
      </pre>
    </details>
  );
}

function MailAddressChangeDetails({ meta }: { readonly meta: Record<string, unknown> }): JSX.Element {
  const surfaced = new Set(['trackingNumber', 'newLine1', 'newCity', 'newState', 'newPostalCode', 'crossZone', 'reason', 'redirectFeeNgn']);
  const crossZone = meta['crossZone'];
  return (
    <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Tracking number" value={readStr(meta, 'trackingNumber')} />
      <Field label="Cross-zone" value={crossZone === true ? 'YES — supervisor approval required' : crossZone === false ? 'No' : null} />
      <Field label="Redirect fee" value={readNgnFromString(meta, 'redirectFeeNgn')} />
      <Field label="New address line 1" value={readStr(meta, 'newLine1')} />
      <Field label="New city" value={readStr(meta, 'newCity')} />
      <Field label="New state" value={readStr(meta, 'newState')} />
      <Field label="Postal code" value={readStr(meta, 'newPostalCode')} />
      <div className="md:col-span-3">
        <Field label="Reason given" value={readStr(meta, 'reason')} />
      </div>
      <div className="md:col-span-3">
        <MetadataResidue meta={meta} surfaced={surfaced} />
      </div>
    </div>
  );
}

function SettlementDetails({ meta }: { readonly meta: Record<string, unknown> }): JSX.Element {
  const surfaced = new Set(['settlementNumber', 'counterpartyType', 'counterpartyId', 'counterpartyName', 'currency', 'grossAmountMinor', 'feeAmountMinor', 'netAmountMinor', 'periodStart', 'periodEnd', 'txnCount']);
  return (
    <div className="bg-purple-50/40 border border-purple-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Settlement #" value={readStr(meta, 'settlementNumber')} />
      <Field label="Counterparty" value={[readStr(meta, 'counterpartyName'), readStr(meta, 'counterpartyType')].filter(Boolean).join(' · ') || null} />
      <Field label="Currency" value={readStr(meta, 'currency') ?? 'NGN'} />
      <Field label="Gross" value={readNgnFromMinor(meta, 'grossAmountMinor')} />
      <Field label="Fee" value={readNgnFromMinor(meta, 'feeAmountMinor')} />
      <Field label="Net (paying)" value={readNgnFromMinor(meta, 'netAmountMinor')} />
      <Field label="Period start" value={readStr(meta, 'periodStart')} />
      <Field label="Period end" value={readStr(meta, 'periodEnd')} />
      <Field label="Txn count" value={readStr(meta, 'txnCount')} />
      <div className="md:col-span-3">
        <MetadataResidue meta={meta} surfaced={surfaced} />
      </div>
    </div>
  );
}

function FranchiseApplicationDetails({ meta }: { readonly meta: Record<string, unknown> }): JSX.Element {
  const surfaced = new Set(['franchiseId', 'requestedTier', 'formData', 'checks', 'decisionNote', 'decidedBy', 'decidedAt']);
  const formData = meta['formData'];
  const checks = meta['checks'];
  return (
    <div className="bg-teal-50/40 border border-teal-200 rounded-lg p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Franchise ID" value={readStr(meta, 'franchiseId')} />
        <Field label="Requested tier" value={readStr(meta, 'requestedTier')} />
        <Field label="Decision note" value={readStr(meta, 'decisionNote')} />
      </div>
      {formData !== null && formData !== undefined ? (
        <details open>
          <summary className="cursor-pointer text-[10px] uppercase text-muted font-semibold tracking-wide hover:text-gray-700">Application form data</summary>
          <pre className="mt-2 p-2 bg-white border border-border rounded-lg text-[10px] font-mono whitespace-pre-wrap break-all overflow-x-auto">
            {JSON.stringify(formData, null, 2)}
          </pre>
        </details>
      ) : null}
      {checks !== null && checks !== undefined ? (
        <details open>
          <summary className="cursor-pointer text-[10px] uppercase text-muted font-semibold tracking-wide hover:text-gray-700">Compliance / KYC checks</summary>
          <pre className="mt-2 p-2 bg-white border border-border rounded-lg text-[10px] font-mono whitespace-pre-wrap break-all overflow-x-auto">
            {JSON.stringify(checks, null, 2)}
          </pre>
        </details>
      ) : null}
      <MetadataResidue meta={meta} surfaced={surfaced} />
    </div>
  );
}

function FranchiseTierUpgradeDetails({ meta }: { readonly meta: Record<string, unknown> }): JSX.Element {
  const surfaced = new Set(['franchiseId', 'fromTier', 'toTier', 'direction']);
  return (
    <div className="bg-indigo-50/40 border border-indigo-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-3">
      <Field label="Franchise ID" value={readStr(meta, 'franchiseId')} />
      <Field label="From tier" value={readStr(meta, 'fromTier')} />
      <Field label="To tier" value={readStr(meta, 'toTier')} />
      <Field label="Direction" value={readStr(meta, 'direction')} />
      <div className="md:col-span-4">
        <MetadataResidue meta={meta} surfaced={surfaced} />
      </div>
    </div>
  );
}

function DetailsPanel({ row }: { readonly row: ApprovalRowDto }): JSX.Element {
  const meta = row.metadata ?? {};
  switch (row.type) {
    case 'MAIL_ADDRESS_CHANGE':
      return <MailAddressChangeDetails meta={meta} />;
    case 'SETTLEMENT':
      return <SettlementDetails meta={meta} />;
    case 'FRANCHISE_APPLICATION':
      return <FranchiseApplicationDetails meta={meta} />;
    case 'FRANCHISE_TIER_UPGRADE':
      return <FranchiseTierUpgradeDetails meta={meta} />;
    default:
      // Defensive fallback if the aggregator adds a new type we don't render
      // explicitly yet — show the raw metadata so reviewers still see it.
      return (
        <pre className="p-3 bg-white border border-border rounded-lg text-[10px] font-mono whitespace-pre-wrap break-all overflow-x-auto">
          {JSON.stringify(meta, null, 2)}
        </pre>
      );
  }
}
